import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Home, Timer, Play, Pause, RefreshCw, 
  Trophy, Star, Zap, Volume2, CheckCircle2
} from 'lucide-react';

const timedQuestions = [
  {
    id: 1,
    question: 'Kendinizi tanıtır mısınız?',
    timeLimit: 60,
    difficulty: 'easy',
    tips: ['30-60 saniye ideal', 'İsim, deneyim, motivasyon']
  },
  {
    id: 2,
    question: 'Neden kabin memuru olmak istiyorsunuz?',
    timeLimit: 60,
    difficulty: 'easy',
    tips: ['Tutku + beceri + hedef', 'Samimi ol']
  },
  {
    id: 3,
    question: 'Zor bir müşteriyle yaşadığınız bir deneyimi anlatın.',
    timeLimit: 90,
    difficulty: 'medium',
    tips: ['STAR tekniği kullan', 'Sonuç odaklı ol']
  },
  {
    id: 4,
    question: 'Stresli bir durumda nasıl sakin kalırsınız?',
    timeLimit: 60,
    difficulty: 'medium',
    tips: ['Somut örnek ver', 'Teknik açıkla']
  },
  {
    id: 5,
    question: 'Bir uçuşta yolcu fenalaştı. Ne yaparsınız?',
    timeLimit: 90,
    difficulty: 'hard',
    tips: ['Adım adım anlat', 'Güvenlik önce']
  },
  {
    id: 6,
    question: 'Why do you want to work for this airline?',
    timeLimit: 60,
    difficulty: 'medium',
    tips: ['Araştırma yaptığını göster', 'Spesifik ol']
  },
  {
    id: 7,
    question: 'Tell me about a time you worked in a team.',
    timeLimit: 90,
    difficulty: 'medium',
    tips: ['Rolünü vurgula', 'Sonucu paylaş']
  },
  {
    id: 8,
    question: 'Bir yolcu alkollü ve kavgacı. Nasıl müdahale edersiniz?',
    timeLimit: 120,
    difficulty: 'hard',
    tips: ['Sakinlik', 'Prosedür', 'Takım çalışması']
  },
  {
    id: 9,
    question: 'Güçlü ve zayıf yönleriniz nelerdir?',
    timeLimit: 90,
    difficulty: 'medium',
    tips: ['Dürüst ol', 'Zayıflığı geliştirme olarak sun']
  },
  {
    id: 10,
    question: '5 yıl sonra kendinizi nerede görüyorsunuz?',
    timeLimit: 60,
    difficulty: 'easy',
    tips: ['Havacılık odaklı', 'Gerçekçi hedefler']
  }
];

export default function InterviewTimedPractice({ onBack }) {
  const [mode, setMode] = useState('menu'); // menu, practice, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [speaking, setSpeaking] = useState(false);
  
  const timerRef = useRef(null);

  const filteredQuestions = selectedDifficulty === 'all' 
    ? timedQuestions 
    : timedQuestions.filter(q => q.difficulty === selectedDifficulty);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isRunning) {
      handleTimeUp();
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, timeLeft]);

  const startPractice = () => {
    setCurrentQuestion(0);
    setResults([]);
    setTimeLeft(filteredQuestions[0]?.timeLimit || 60);
    setMode('practice');
    setIsRunning(false);
  };

  const startTimer = () => {
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleTimeUp = () => {
    setIsRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
    
    const question = filteredQuestions[currentQuestion];
    const timeUsed = question.timeLimit - timeLeft;
    
    setResults([...results, {
      questionId: question.id,
      question: question.question,
      timeLimit: question.timeLimit,
      timeUsed: question.timeLimit, // Full time used
      completed: false
    }]);
  };

  const markComplete = () => {
    setIsRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
    
    const question = filteredQuestions[currentQuestion];
    const timeUsed = question.timeLimit - timeLeft;
    
    setResults([...results, {
      questionId: question.id,
      question: question.question,
      timeLimit: question.timeLimit,
      timeUsed: timeUsed,
      completed: true
    }]);
    
    // Move to next question or results
    if (currentQuestion < filteredQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTimeLeft(filteredQuestions[currentQuestion + 1].timeLimit);
      setIsRunning(false);
    } else {
      setMode('results');
    }
  };

  const skipQuestion = () => {
    if (currentQuestion < filteredQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTimeLeft(filteredQuestions[currentQuestion + 1].timeLimit);
      setIsRunning(false);
    } else {
      setMode('results');
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = text.match(/^[a-zA-Z]/) ? 'en-US' : 'tr-TR';
      utterance.rate = 0.9;
      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDifficultyLabel = (diff) => {
    switch (diff) {
      case 'easy': return 'Kolay';
      case 'medium': return 'Orta';
      case 'hard': return 'Zor';
      default: return diff;
    }
  };

  // Menu Screen
  if (mode === 'menu') {
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-br from-orange-600 to-red-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Pratik Merkezi</span>
            </button>
            
            <div className="text-center text-white">
              <Timer className="w-12 h-12 mx-auto mb-2 text-yellow-300" />
              <h1 className="text-2xl font-bold">Zamanlı Pratik</h1>
              <p className="text-orange-100 text-sm mt-1">Süre baskısı altında cevap ver</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 mt-6">
          {/* Difficulty Filter */}
          <div className="flex justify-center gap-2 mb-6">
            {['all', 'easy', 'medium', 'hard'].map((diff) => (
              <button
                key={diff}
                onClick={() => setSelectedDifficulty(diff)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedDifficulty === diff 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {diff === 'all' ? 'Tümü' : getDifficultyLabel(diff)}
              </button>
            ))}
          </div>

          {/* Question Preview */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 mb-3">Sorular ({filteredQuestions.length})</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredQuestions.map((q, i) => (
                  <div key={q.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm text-gray-700 line-clamp-1">{q.question}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getDifficultyColor(q.difficulty)}`}>
                      {q.timeLimit}s
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Start Button */}
          <Button 
            onClick={startPractice}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 py-6 text-lg"
          >
            <Zap className="w-5 h-5 mr-2" />
            Pratiğe Başla
          </Button>

          {/* Info */}
          <Card className="mt-6 bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-gray-800 mb-2">⏱️ Nasıl Çalışır?</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Her soru için belirli süre verilir</li>
                <li>• Başlat\'a tıklayınca sayaç başlar</li>
                <li>• Süre dolmadan "Cevapladım" de</li>
                <li>• Gerçek mülakat simülasyonu</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Practice Screen
  if (mode === 'practice') {
    const question = filteredQuestions[currentQuestion];
    const progress = ((currentQuestion) / filteredQuestions.length) * 100;
    const timeProgress = (timeLeft / question.timeLimit) * 100;
    const isLowTime = timeLeft <= 10;

    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <button 
            onClick={() => setMode('menu')}
            className="text-white/80 hover:text-white flex items-center gap-2"
          >
            <Home className="w-5 h-5" />
            Çık
          </button>
          <div className="text-white text-sm">
            Soru {currentQuestion + 1} / {filteredQuestions.length}
          </div>
        </div>

        {/* Progress */}
        <div className="px-4">
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          {/* Timer */}
          <div className={`text-8xl font-bold mb-4 ${isLowTime ? 'text-red-500 animate-pulse' : 'text-white'}`}>
            {formatTime(timeLeft)}
          </div>
          
          {/* Time Progress */}
          <div className="w-full max-w-md mb-8">
            <div className="bg-gray-700 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all ${isLowTime ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${timeProgress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <Card className="w-full max-w-md mb-6">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(question.difficulty)}`}>
                  {getDifficultyLabel(question.difficulty)}
                </span>
                <button 
                  style={{display: "none"}}
                  className={`p-2 rounded-full ${speaking ? 'bg-blue-100' : 'bg-gray-100'}`}
                >
                  <Volume2 className={`w-5 h-5 ${speaking ? 'text-blue-600' : 'text-gray-600'}`} />
                </button>
              </div>
              <h2 className="text-xl font-bold text-gray-900">{question.question}</h2>
              
              <div className="mt-4 bg-yellow-50 rounded-lg p-3">
                <p className="text-sm text-yellow-800 font-medium mb-1">💡 İpuçları:</p>
                <ul className="text-sm text-gray-600">
                  {question.tips.map((tip, i) => (
                    <li key={i}>• {tip}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Controls */}
          <div className="flex gap-4">
            {!isRunning ? (
              <Button 
                onClick={startTimer}
                className="bg-green-600 hover:bg-green-700 px-8 py-6"
              >
                <Play className="w-6 h-6 mr-2" />
                Başlat
              </Button>
            ) : (
              <>
                <Button 
                  onClick={pauseTimer}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                >
                  <Pause className="w-5 h-5 mr-2" />
                  Duraklat
                </Button>
                <Button 
                  onClick={markComplete}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Cevapladım
                </Button>
              </>
            )}
          </div>

          {/* Skip */}
          <button 
            onClick={skipQuestion}
            className="mt-4 text-gray-400 hover:text-white text-sm"
          >
            Soruyu Atla →
          </button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (mode === 'results') {
    const completedCount = results.filter(r => r.completed).length;
    const avgTimeUsed = results.length > 0 
      ? Math.round(results.reduce((acc, r) => acc + r.timeUsed, 0) / results.length)
      : 0;

    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-br from-green-600 to-emerald-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Trophy className="w-16 h-16 mx-auto mb-2 text-yellow-300" />
            <h1 className="text-2xl font-bold">Pratik Tamamlandı!</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 mt-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{completedCount}</div>
                <div className="text-sm text-gray-600">Cevaplanan</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{results.length}</div>
                <div className="text-sm text-gray-600">Toplam</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">{avgTimeUsed}s</div>
                <div className="text-sm text-gray-600">Ort. Süre</div>
              </CardContent>
            </Card>
          </div>

          {/* Results List */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 mb-3">Detaylı Sonuçlar</h3>
              <div className="space-y-2">
                {results.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      r.completed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {r.completed ? <CheckCircle2 className="w-5 h-5" /> : <Timer className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 line-clamp-1">{r.question}</p>
                      <p className="text-xs text-gray-500">
                        {r.timeUsed}s / {r.timeLimit}s
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Button 
              variant="outline"
              className="flex-1"
              onClick={() => setMode('menu')}
            >
              Ana Menü
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-orange-600 to-red-600"
              onClick={startPractice}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Tekrar Dene
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
